import { useMutation } from '@apollo/client';
import {
  DefaultButton,
  PrimaryButton,
  Stack,
  TextField,
  TooltipHost,
  Text,
  Icon,
  IconButton,
  useTheme,
} from '@fluentui/react';
import { ConfirmDialog } from 'common/components/ConfirmDialog';
import { cutToast } from 'utility/toast';
import { FutureDatePicker } from 'common/components/FutureDatePicker';
import { UrgencyToggle } from 'common/components/UrgencySelector/UrgencyToggle';
import { dateFormat } from 'common/utils/dateFormats';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import {
  ApprovalSetupApprovalCreate,
  ApprovalSetupApprovalCreateVariables,
} from 'settings/account/approvalSetup/view/__generated__/ApprovalSetupApprovalCreate';
import {
  ApprovalSetupApprovalRevoke,
  ApprovalSetupApprovalRevokeVariables,
} from 'settings/account/approvalSetup/view/__generated__/ApprovalSetupApprovalRevoke';
import { ApprovalsInfo_Query } from '../../__generated__/ApprovalsInfo';
import { FlowApproval_Query } from '../../__generated__/FlowApproval';
import { APPROVAL_SETUP_INITIAL_VALUES } from '../constants';
import { approvalFlowSidebarEnums } from '../Sidebar';

import GET_APPROVAL_DETAILS from '../../FlowApproval.graphql';
import APPROVAL_SETUP_APPROVAL_CREATE from '../../../approvalSetup/view/ApprovalSetupApprovalCreate.graphql';
import APPROVAL_SETUP_APPROVAL_REVOKE from '../../../approvalSetup/view/ApprovalSetupApprovalRevoke.graphql';
import { useStyles } from './index.styles';
import clsx from 'clsx';
import { TransactionsApprovalModal } from '../TransactionsApprovalModal';
import { globalMode, setGlobalSearchText } from 'utility/cache/ui';
const CONFIRM_AMEND_DIALOG_TITLE = 'Are you sure?';
const CONFIRM_AMEND_DIALOG_SUBTEXT =
  'This will remove the Approval setup from the approval cycle and require re-approval.';

const CONFIRM_REQUEST_DIALOG_TITLE =
  'Are you sure you want to process this approval setup?';
const CONFIRM_REQUEST_DIALOG_SUBTEXT = 'Your approval setup will be processed.';
const FINALIZED_BY_DATE_TOOLTIP =
  'Automatically promotes to urgent notifications when not approved by this date';

export const Footer: React.FC<{
  onCancel: () => void;
  onSave: () => void;
  approvalDetailsData: FlowApproval_Query | undefined;
  isNew: boolean;
  menuSelected: approvalFlowSidebarEnums;
  selectedKey: string;
}> = ({
  onCancel,
  onSave,
  approvalDetailsData,
  isNew,
  menuSelected,
  selectedKey,
}) => {
  const history = useHistory();
  const styles = useStyles();
  const theme = useTheme();
  const {
    reset,
    formState: { isDirty, errors },
  } = useFormContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hideConfirmAmendDialog, setHideConfirmAmendDialog] =
    useState<boolean>(true);
  const toggleConfirmAmendDialog = () =>
    setHideConfirmAmendDialog((prevState) => !prevState);
  const [hideConfirmRequestDialog, setHideConfirmRequestDialog] =
    useState<boolean>(true);
  const toggleConfirmDialog = () =>
    setHideConfirmRequestDialog((prevState) => !prevState);
  const [requestComment, setRequestComment] = useState<string>();
  const [requiredDate, setRequiredDate] = useState<string | undefined>();
  const [error, setError] = useState(false);
  const [urgencyLevel, setUrgencyLevel] = useState<number>(1);
  const onProcess = async () => {
    toggleConfirmDialog();
    const { id, _rowTimestamp } = { ...approvalDetailsData?.approval };

    const { errors } = await approvalSetupApprovalCreate({
      variables: {
        input: {
          entityApproval: [
            {
              entityId: id!,
              rowTimestamp: _rowTimestamp!,
              comments: requestComment,
              requiredDate,
            },
          ],
          urgencyLevel: urgencyLevel,
        },
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GET_APPROVAL_DETAILS,
          variables: {
            id: id!,
          },
        },
      ],
    });

    if (errors?.length) cutToast.error(errors[0].message);
    else {
      setRequestComment('');
      setRequiredDate(undefined);
      cutToast.success('Request sent for approval');
    }
  };
  const [
    approvalSetupApprovalCreate,
    { loading: approvalSetupApprovalCreateLoading },
  ] = useMutation<
    ApprovalSetupApprovalCreate,
    ApprovalSetupApprovalCreateVariables
  >(APPROVAL_SETUP_APPROVAL_CREATE, {
    errorPolicy: 'all',
    onCompleted: () => {
      setUrgencyLevel(1);
    },
  });

  const [
    approvalSetupApprovalRevoke,
    { loading: approvalSetupApprovalRevokeLoading },
  ] = useMutation<
    ApprovalSetupApprovalRevoke,
    ApprovalSetupApprovalRevokeVariables
  >(APPROVAL_SETUP_APPROVAL_REVOKE, { errorPolicy: 'all' });
  const onAmend = async () => {
    toggleConfirmAmendDialog();
    const { errors } = await approvalSetupApprovalRevoke({
      variables: {
        input: {
          entityId: approvalDetailsData?.approval?.id!,
          rowTimestamp: approvalDetailsData?.approval?._rowTimestamp!,
        },
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GET_APPROVAL_DETAILS,
          variables: {
            id: approvalDetailsData?.approval?.id!,
          },
        },
      ],
    });
    if (errors?.length) cutToast.error(errors[0].message);
    else cutToast.success('Approval amended successfully');
  };

  return (
    <Stack
      horizontal
      style={{
        width: '100%',
        position: 'absolute',
        right: 0,
        bottom: 0,
        paddingBottom: 30,
      }}
    >
      <Stack
        horizontal
        horizontalAlign="end"
        style={{
          padding: '10px 20px',
          marginLeft: 'auto',
        }}
        tokens={{ childrenGap: 20 }}
      >
        {menuSelected === approvalFlowSidebarEnums.generalInfo &&
          selectedKey === 'BASIC' &&
          !!approvalDetailsData?.approval?._transactionApprovalCount && (
            <Stack
              horizontal
              verticalAlign="center"
              tokens={{ childrenGap: 10 }}
              style={{
                cursor: 'pointer',
              }}
              onClick={() => {
                globalMode(true);
                setIsModalOpen(true);
              }}
            >
              <Icon iconName="CheckList" />
              <Text
                variant="xLarge"
                style={{
                  color: isModalOpen
                    ? theme.palette.neutralTertiary
                    : undefined,
                }}
              >
                Transaction Approvals
              </Text>
              <Stack className={clsx(styles.primaryBadge)}>
                <Text className={styles.countText}>
                  {approvalDetailsData?.approval?._transactionApprovalCount}
                </Text>
              </Stack>
            </Stack>
          )}
        <PrimaryButton
          text="Save"
          disabled={!isDirty || !!Object.entries(errors).length}
          onClick={() => onSave()}
        />
        <DefaultButton
          text="Cancel"
          onClick={() => {
            onCancel();
          }}
        />
        {menuSelected === approvalFlowSidebarEnums.generalInfo && (
          <Stack horizontal horizontalAlign="end" tokens={{ childrenGap: 20 }}>
            {approvalDetailsData?.approval?._isStagedApprovalRequest && (
              <PrimaryButton
                text="Request Approval"
                onClick={toggleConfirmDialog}
              />
            )}
            {approvalDetailsData?.approval?._isApprovalRevocable && (
              <PrimaryButton
                text="Amend Approval"
                onClick={toggleConfirmAmendDialog}
              />
            )}
            <ConfirmDialog
              hidden={hideConfirmAmendDialog}
              title={CONFIRM_AMEND_DIALOG_TITLE}
              subText={CONFIRM_AMEND_DIALOG_SUBTEXT}
              onDismiss={toggleConfirmAmendDialog}
              onConfirm={onAmend}
            />

            {!isNew && (
              <PrimaryButton
                text="New Approval"
                onClick={() => {
                  reset({
                    ...APPROVAL_SETUP_INITIAL_VALUES,
                    approvalTypeId:
                      approvalDetailsData?.approval?.approvalTypeId,
                  });
                  history.push(`/account-management/approval-setup?id=`);
                }}
              />
            )}
            <ConfirmDialog
              hidden={hideConfirmRequestDialog}
              successBtnDisable={error}
              title={CONFIRM_REQUEST_DIALOG_TITLE}
              subText={CONFIRM_REQUEST_DIALOG_SUBTEXT}
              onDismiss={toggleConfirmDialog}
              minWidth={500}
              onConfirm={onProcess}
            >
              <Stack tokens={{ childrenGap: 10 }}>
                <TextField
                  multiline
                  rows={3}
                  value={requestComment}
                  placeholder="Please write your comment here (optional)"
                  resizable={false}
                  onChange={(_event, value) => setRequestComment(value || '')}
                />
                <TooltipHost content={FINALIZED_BY_DATE_TOOLTIP}>
                  <FutureDatePicker
                    onSelectDate={(date) => {
                      if (date) {
                        setRequiredDate(dateFormat(date!.toString()));
                      } else setRequiredDate(undefined);
                    }}
                    onValidationError={(error) => {
                      error ? setError(true) : setError(false);
                    }}
                    customPlaceholder="Needed by date (optional)"
                  />
                </TooltipHost>
                <Stack tokens={{ padding: '6px 0px 0px 0px' }}>
                  <UrgencyToggle
                    onToggle={(data) => {
                      setUrgencyLevel(data ? 0 : 1);
                    }}
                  />
                </Stack>
              </Stack>
            </ConfirmDialog>
          </Stack>
        )}
      </Stack>
      {isModalOpen && approvalDetailsData?.approval?.id && (
        <TransactionsApprovalModal
          filterApprovalId={approvalDetailsData?.approval?.id}
          isModalOpen={isModalOpen}
          onDismiss={() => {
            setGlobalSearchText('');
            setIsModalOpen(false);
          }}
        />
      )}
    </Stack>
  );
};
